import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getApiBaseUrl } from "../../config/apiBase";
import { AuthenticatedFileLinks } from "../../components/AuthenticatedFileLinks";
import { SeedingConfirmModal } from "../../components/SeedingConfirmModal";
import { useAppState } from "../../context/AppStateContext";
import type { HoBankingPatch, MouAttachmentRef } from "../../types";
import { useToast } from "../../context/ToastContext";
import { readFileAsRawBase64 } from "../../utils/readFileAsRawBase64";
import { hoBankingChecks, hoBankingPatchHasChanges, hoBankingStatusHint } from "../../utils/hoBankingProgress";

function mouFileUrl(applicationId: string, att?: MouAttachmentRef): string {
  const base = `${getApiBaseUrl()}/api/applications/${encodeURIComponent(applicationId)}/mou-file`;
  if (att?.id) return `${base}/${encodeURIComponent(att.id)}`;
  return base;
}

type IciciBaseline = { account: string; ifsc: string; branch: string };

export function BankingTab() {
  const { id } = useParams<{ id: string }>();
  const { getById, updateHo, saveIciciDetails, confirmSeeding } = useAppState();
  const { showToast } = useToast();
  const app = id ? getById(id) : undefined;
  const [beforeIciciModal, setBeforeIciciModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<string[]>([]);
  const iciciBaselineRef = useRef<IciciBaseline>({ account: "", ifsc: "", branch: "" });

  useEffect(() => {
    setPendingFiles([]);
    setRemovedAttachmentIds([]);
    if (!app) return;
    iciciBaselineRef.current = {
      account: app.ho.iciciAccountNumber.trim(),
      ifsc: app.ho.iciciIfsc.trim(),
      branch: app.ho.iciciBranch.trim(),
    };
  }, [app?.id]);

  if (!app) return null;
  const ho = app.ho;
  const v = app.validations;
  const banking = hoBankingChecks({
    ...ho,
    mouFilesUpload:
      pendingFiles.length > 0
        ? pendingFiles.map((f) => ({ fileName: f.name, mouFileBase64: "", mouMimeType: null }))
        : ho.mouFilesUpload,
  });
  const statusHint = hoBankingStatusHint({
    ...app,
    ho: {
      ...ho,
      mouFilesUpload:
        pendingFiles.length > 0
          ? pendingFiles.map((f) => ({ fileName: f.name, mouFileBase64: "", mouMimeType: null }))
          : ho.mouFilesUpload,
    },
  });

  const storedAttachments = (ho.mouAttachments ?? []).filter((a) => !removedAttachmentIds.includes(a.id));

  const buildHoBankingPatch = async (): Promise<HoBankingPatch> => {
    const latest = id ? getById(id) : undefined;
    if (!latest) throw new Error("Application not found");
    const patch: HoBankingPatch = {};
    const base = iciciBaselineRef.current;
    const account = latest.ho.iciciAccountNumber.trim();
    const ifsc = latest.ho.iciciIfsc.trim();
    const branch = latest.ho.iciciBranch.trim();
    if (account !== base.account) patch.iciciAccountNumber = account;
    if (ifsc !== base.ifsc) patch.iciciIfsc = ifsc;
    if (branch !== base.branch) patch.iciciBranch = branch;

    if (pendingFiles.length > 0) {
      patch.mouFilesUpload = await Promise.all(
        pendingFiles.map(async (f) => {
          const { base64, mimeType } = await readFileAsRawBase64(f);
          return { fileName: f.name, mouFileBase64: base64, mouMimeType: mimeType };
        }),
      );
    }
    if (removedAttachmentIds.length) {
      patch.mouRemovedAttachmentIds = [...removedAttachmentIds];
    }
    return patch;
  };

  const syncIciciBaselineFromApp = (applicationId: string) => {
    const saved = getById(applicationId);
    if (!saved) return;
    iciciBaselineRef.current = {
      account: saved.ho.iciciAccountNumber.trim(),
      ifsc: saved.ho.iciciIfsc.trim(),
      branch: saved.ho.iciciBranch.trim(),
    };
  };

  const openSave = async () => {
    const current = id ? getById(id) : undefined;
    if (!current) return;
    if (!current.validations.aadhaarSeedingConfirmed) {
      setBeforeIciciModal(true);
      return;
    }
    try {
      const patch = await buildHoBankingPatch();
      if (!hoBankingPatchHasChanges(patch)) {
        showToast("No changes to save.", "info");
        return;
      }
      await saveIciciDetails(current.id, patch);
      syncIciciBaselineFromApp(current.id);
      setPendingFiles([]);
      setRemovedAttachmentIds([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch {
      /* saveIciciDetails shows error toast */
    }
  };

  const selectionHint = (() => {
    const parts: string[] = [];
    if (storedAttachments.length) {
      parts.push(storedAttachments.map((a) => a.fileName).join(", "));
    }
    if (pendingFiles.length) {
      parts.push(pendingFiles.map((f) => f.name).join(", "));
    }
    return parts.filter(Boolean).join(" · ");
  })();

  return (
    <>
      <p className="tab-intro">
        <strong>What you do here:</strong> Type the ICICI account number, IFSC, and branch, then attach the signed MOU file.
        This tab stays <strong>greyed out</strong> until Aadhaar seeding is confirmed on the <strong>Verification</strong>{" "}
        tab. Use <strong>Save banking &amp; MOU</strong> below when you are finished — changes are not saved until you
        press it.
      </p>

      {!v.aadhaarSeedingConfirmed ? (
        <div className="alert alert--info">Seeding confirmation is required before these fields can be used.</div>
      ) : null}

      {v.aadhaarSeedingConfirmed && (app.status === "ready_for_icici" || app.status === "ready_for_mou") ? (
        <div className="alert alert--info" role="status">
          <p style={{ margin: "0 0 0.5rem" }}>
            <strong>Pipeline checklist</strong> (all must be complete before status moves forward):
          </p>
          <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
            <li>{banking.iciciAccount ? "ICICI account number — done" : "ICICI account number — missing"}</li>
            <li>{banking.iciciIfsc ? "ICICI IFSC — done" : "ICICI IFSC — missing"}</li>
            <li>{banking.iciciBranch ? "ICICI branch — done" : "ICICI branch — missing"}</li>
            <li>{banking.mouAttached ? "MOU file — attached" : "MOU file — not attached yet"}</li>
          </ul>
          {statusHint ? (
            <p className="text-muted section-lead--tight" style={{ marginTop: "0.65rem", marginBottom: 0 }}>
              {statusHint}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="card card--gov card--elevated">
        <h2 className="card__title card__title--gov">ICICI account & branch</h2>
        <div className="field-grid">
          <div className="field">
            <label>ICICI account number</label>
            <input
              className="mono"
              value={ho.iciciAccountNumber}
              disabled={!v.aadhaarSeedingConfirmed}
              onChange={(e) => updateHo(app.id, { iciciAccountNumber: e.target.value })}
            />
          </div>
          <div className="field">
            <label>ICICI IFSC</label>
            <input
              className="mono"
              value={ho.iciciIfsc}
              disabled={!v.aadhaarSeedingConfirmed}
              onChange={(e) => updateHo(app.id, { iciciIfsc: e.target.value })}
            />
          </div>
          <div className="field">
            <label>ICICI branch</label>
            <input
              value={ho.iciciBranch}
              disabled={!v.aadhaarSeedingConfirmed}
              onChange={(e) => updateHo(app.id, { iciciBranch: e.target.value })}
            />
          </div>
        </div>
      </div>

      <div className="card card--gov card--elevated">
        <h2 className="card__title card__title--gov">MOU scan (signed copy)</h2>
        <div className="field field--full">
          <label>Upload MOU (one or more files)</label>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,image/*"
            disabled={!v.aadhaarSeedingConfirmed}
            onChange={(e) => {
              const picked = [...(e.target.files ?? [])];
              if (!picked.length) return;
              const merged = [...pendingFiles, ...picked];
              setPendingFiles(merged);
              updateHo(app.id, { mouFileName: merged.map((f) => f.name).join(", ") || null });
              e.target.value = "";
            }}
          />
          {selectionHint ? <span className="text-muted file-hint">Selected: {selectionHint}</span> : null}

          {storedAttachments.length > 0 ? (
            <ul className="file-hint" style={{ marginTop: "0.75rem", paddingLeft: "1.25rem" }}>
              {storedAttachments.map((att) => (
                <li key={att.id} style={{ marginBottom: "0.35rem" }}>
                  <span className="text-muted">{att.fileName}</span>
                  <span className="text-muted"> — </span>
                  <AuthenticatedFileLinks fileUrlBase={mouFileUrl(app.id, att)} fallbackFileName={att.fileName} />
                  <span className="text-muted"> · </span>
                  <button
                    type="button"
                    className="summary-card__link"
                    style={{ background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit" }}
                    onClick={() => {
                      const nextIds = [...removedAttachmentIds, att.id];
                      setRemovedAttachmentIds(nextIds);
                      const still = (ho.mouAttachments ?? []).filter((a) => !nextIds.includes(a.id));
                      const names = [...still.map((a) => a.fileName), ...pendingFiles.map((f) => f.name)].join(", ");
                      updateHo(app.id, { mouFileName: names || null });
                    }}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          {ho.mouFileStored && !storedAttachments.length && !(ho.mouAttachments?.length) ? (
            <div className="text-muted file-hint" style={{ marginTop: "0.5rem" }}>
              <AuthenticatedFileLinks fileUrlBase={mouFileUrl(app.id)} fallbackFileName={ho.mouFileName ?? "mou-file"} />
            </div>
          ) : null}

          {pendingFiles.length > 0 ? (
            <ul className="file-hint" style={{ marginTop: "0.5rem", paddingLeft: "1.25rem" }}>
              {pendingFiles.map((f, idx) => (
                <li key={`${f.name}-${idx}`}>
                  <span className="text-muted">{f.name} (not saved yet)</span>
                  <span className="text-muted"> · </span>
                  <button
                    type="button"
                    className="summary-card__link"
                    style={{ background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit" }}
                    onClick={() => {
                      const next = pendingFiles.filter((_, i) => i !== idx);
                      setPendingFiles(next);
                      const label = [...storedAttachments.map((a) => a.fileName), ...next.map((f) => f.name)].join(", ");
                      updateHo(app.id, { mouFileName: label || null });
                    }}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>

      <div className="toolbar-row">
        <button type="button" className="btn btn--primary btn--tg" onClick={() => void openSave()} disabled={!v.aadhaarSeedingConfirmed}>
          Save banking &amp; MOU
        </button>
      </div>

      <SeedingConfirmModal
        open={beforeIciciModal}
        title="Aadhaar seeding required"
        body="Seeding must be confirmed before saving ICICI details. Confirm seeding now to continue, or cancel to return."
        confirmLabel="Confirm seeding and save"
        onCancel={() => setBeforeIciciModal(false)}
        onConfirm={() => {
          void (async () => {
            try {
              await confirmSeeding(app.id);
              const current = id ? getById(id) : undefined;
              if (!current) return;
              const patch = await buildHoBankingPatch();
              if (!hoBankingPatchHasChanges(patch)) {
                showToast("No changes to save.", "info");
                return;
              }
              await saveIciciDetails(current.id, patch);
              syncIciciBaselineFromApp(current.id);
              setPendingFiles([]);
              setRemovedAttachmentIds([]);
              if (fileInputRef.current) fileInputRef.current.value = "";
            } catch {
              /* confirmSeeding / saveIciciDetails show error toast */
            } finally {
              setBeforeIciciModal(false);
            }
          })();
        }}
      />
    </>
  );
}
